import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, Settings } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  HomeworkConfigStep,
  ResourcesAndAssessmentStep,
} from "./homework/steps";
import HomeworkSpecificFields from "./homework/HomeworkSpecificFields";
import { HomeworkIdeaCreateRequestDto } from "@packages/api/madlenAPI.schemas";

interface HomeworkCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string; // Optional since it's hardcoded to "lime"
}

export const HomeworkCreateModal: React.FC<HomeworkCreateModalProps> = ({
  onSubmit,
  initialInput,
  ...props
}) => {
  const { t } = useTranslation();

  // State for basic homework fields
  const [basicContext, setBasicContext] = useState(
    (initialInput as any).context || "",
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: HomeworkIdeaCreateRequestDto = {
    // Basic CreateModal data (will be populated from first step)
    courseName: initialInput.courseName as any,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType: initialInput.generationType as any,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage: initialInput.outputLanguage as any,

    // Advanced homework-specific defaults
    homeworkPurpose: undefined,
    customDuration: undefined,
    difficultyLevel: undefined,
    workType: undefined,

    // Advanced resources defaults
    resourcePreferences: [],

    // Evaluation guidelines defaults
    evaluationGuidelines: {
      focusAreas: [],
      includeTeacherGuidelines: false,
      includeStudentCriteria: false,
    },
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<HomeworkIdeaCreateRequestDto>[] = [
    {
      id: "homework-config",
      title: t("homework.steps.config.title", "Homework Configuration"),
      description: t(
        "homework.steps.config.description",
        "Define the purpose, duration, and type of homework",
      ),
      icon: <Target size={16} />,
      component: HomeworkConfigStep,
      validation: (data) => {
        // At least one configuration should be selected
        return !!(
          data.homeworkPurpose ||
          data.customDuration ||
          data.difficultyLevel ||
          data.workType
        );
      },
    },
    {
      id: "resources-assessment",
      title: t("homework.steps.resources.title", "Resources & Preferences"),
      description: t(
        "homework.steps.resources.description",
        "Choose preferred resource types for the homework",
      ),
      icon: <Settings size={16} />,
      component: ResourcesAndAssessmentStep,
      validation: (data) => {
        // This step is optional, so always valid
        return true;
      },
    },
  ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    let submissionData = { ...data };

    // Combine context with prompt if context is provided
    if (basicContext.trim()) {
      submissionData.prompt = `${basicContext.trim()}\n\n${data.prompt}`;
    }

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (data: HomeworkIdeaCreateRequestDto) => {
    let submissionData = { ...data };

    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add context if provided from basic context state
    if (basicContext?.trim()) {
      enrichedPrompt = `${basicContext.trim()}\n\n${enrichedPrompt}`;
    }

    // Add homework purpose
    if (data.homeworkPurpose) {
      enrichedPrompt += `\n\nHomework Purpose: ${data.homeworkPurpose}`;
    }

    // Add expected time
    if (data.customDuration) {
      enrichedPrompt += `\nExpected Time: ${data.customDuration}`;
    }

    // Add difficulty
    if (data.difficultyLevel) {
      enrichedPrompt += `\nDifficulty Level: ${data.difficultyLevel}`;
    }

    // Add homework type
    if (data.workType) {
      enrichedPrompt += `\nHomework Type: ${data.workType}`;
    }

    // Add resources
    if (data.resourcePreferences && data.resourcePreferences.length > 0) {
      enrichedPrompt += `\nPreferred Resources: ${data.resourcePreferences.join(", ")}`;
    }

    // Add assessment preferences
    if (data.evaluationGuidelines?.includeTeacherGuidelines) {
      enrichedPrompt += `\nInclude detailed teacher assessment guide.`;
    }

    if (data.evaluationGuidelines?.includeStudentCriteria) {
      enrichedPrompt += `\nInclude student self-assessment criteria.`;
    }

    // Add focus areas
    if (
      data.evaluationGuidelines?.focusAreas &&
      data.evaluationGuidelines.focusAreas.length > 0
    ) {
      enrichedPrompt += `\nFocus Areas: ${data.evaluationGuidelines.focusAreas.join(", ")}`;
    }

    submissionData.prompt = enrichedPrompt;

    await onSubmit(submissionData);
  };

  // Handle context change from basic mode
  const handleContextChange = (context: string) => {
    setBasicContext(context);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<HomeworkIdeaCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "lime",
        onSubmit: handleBasicSubmit,
        after: (
          <HomeworkSpecificFields
            context={basicContext}
            onChange={handleContextChange}
            themeColor="lime"
          />
        ),
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t("quickActions.homeworkCreate", "Create Homework"),
        themeColor: "lime",
        backPath: "/content/homework-ideas",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate", "Generate"),
        advancedModeSubmitText: t(
          "homework.createAdvanced",
          "Create Advanced Homework",
        ),
      },
      themeColor: "lime",
      title: t("quickActions.homeworkCreate", "Create Homework"),
      backPath: "/content/homework-ideas",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default HomeworkCreateModal;
